import * as zod from 'zod';

import { Step } from '@sb/remote-control/types';
import { validateBlockConnectionPosition } from '@sb/remote-control/util/blockConnectorValidation';
import { buildConditional } from '@sb/remote-control/util/conditionalsBuilder';

import { validateConditional } from '../util/conditionalsValidator';

/**
 * Types matching the routine runner schema for "Else If" statements.
 * ElseIf executes its substeps if the previous If/ElseIf condition was false
 * and this condition is true.
 */
export namespace ElseIfStep {
  export const name = 'Else If';
  export const description =
    'Run substeps if previous condition was false and this condition is true';
  export const isDecorator = true;
  export const librarySection = Step.LibrarySection.Hidden;
  export const librarySort = '4';
  export const argumentKind = 'ElseIf';

  /** Marks this step as a continuation of an if/else conditional block for drag grouping */
  export const blockConnector: Step.BlockConnector = {
    blockType: 'ifElse',
    role: 'continuation',
  };

  /** Arguments to be passed to the routine runner */
  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    /**
     * The actual type is ConditionalBuilderState[][] but Firestore doesn't support nested
     * arrays, so we need to serialize/desarialize this property when using it.
     */
    condition: zod.string(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'ElseIf',
      args: {
        condition: buildConditional(JSON.parse(args.condition)),
      },
    };
  };

  export const validator: Step.Validator = ({
    step,
    stepConfiguration,
    routine,
  }) => {
    const args = (stepConfiguration?.args || {}) as Arguments;

    // Validate block position - must be part of a valid if/else block
    validateBlockConnectionPosition(
      step,
      routine,
      blockConnector,
      'Else If must be part of a valid If block',
    );

    // Validate that we have a condition
    if (args.condition == null) return;
    const conditions = JSON.parse(args?.condition || '[]');

    validateConditional(conditions, routine);
  };
}

ElseIfStep satisfies Step.StepKindInfo;
